import type { CloudConfig } from '../../../../../common/constants/filters/cloud-config/types';
import { type SelectFilterState } from '../../../../../common/constants/filters/select-filter/types';
import { ProductKeys, type ProductKeys3P } from '../../../../../common/constants/products';
import { type SelectFilterOption } from '../../../../../common/ui/filters/select-filter';
import { getProducts3PFromConfigForFilter } from '../../../../../common/utils/filters';
import { createMultiSelectFilter } from '../../../../filters/multi-select-filter';

import { i18n, type SalesforceStageKey } from './messages';

/**
 * The set of Salesforce opportunity stage values exposed through the Stage filter.
 *
 * These values are sent as-is to the backend via `salesforceFilters.stage` on the
 * `SearchThirdPartyFilter` GraphQL input, so they must match the values the
 * xpsearch-aggregator / Salesforce integration expects.
 */
const salesforceStages: SalesforceStageKey[] = [
	'New',
	'Closed Won',
	'Lost',
	'Invalid Opp',
	'Closed Won (Auto Linked)',
	'Closed Lost (Send Survey)',
	'No Opportunity',
	'Develop Opportunity',
	'Qualify Opportunity',
];

const salesforceProducts: ProductKeys[] = [ProductKeys.Salesforce];
const products3PFromConfig: ProductKeys3P[] = getProducts3PFromConfigForFilter('salesforceStage');

const getAllStageOptions = (config: CloudConfig): SelectFilterOption[] => {
	return salesforceStages.map((stage) => ({
		value: stage,
		trackingKey: stage,
		queryParamValue: stage,
		isDefault: false,
		label: config.intl?.formatMessage(i18n[stage]) ?? stage,
		product: config.product,
		isSelected: false,
		isSuggested: false,
	}));
};

const fetchQueryParams = (queryParams: string[], config: CloudConfig): SelectFilterOption[] => {
	const allOptions = getAllStageOptions(config);
	return allOptions.filter((option) =>
		queryParams.find((queryParam) => option.value === queryParam),
	);
};

export const filter: Readonly<{
	id: 'salesforceStage';
	type: 'single-select' | 'multi-select';
	queryParams: {
		key: string;
		getState: (
			queryParams: Record<string, string[]>,
			cloudId: string,
		) => SelectFilterState<'salesforceStage'>;
	};
	products: ProductKeys[];
	universal: boolean;
	customValueFields?: string[];
	withState: (
		state: unknown,
		config: CloudConfig,
	) => import('../../../../../common/constants/filters/select-filter/types').SelectFilterMonad<'salesforceStage'>;
	getInitialState: (
		config: CloudConfig,
	) => import('../../../../../common/constants/filters/select-filter/types').SelectFilterMonad<'salesforceStage'>;
	isCompatible: (filterId?: string) => boolean;
	isSupportCustomValue: boolean;
	isSuggestedFilter?: (primaryProduct?: string) => boolean;
}> = createMultiSelectFilter({
	id: 'salesforceStage',
	products: [...salesforceProducts, ...products3PFromConfig],
	alwaysVisible: false,
	universal: false,
	queryParams: {
		key: 'salesforceStages',
		fetcher: async (queryParams, config) => {
			return fetchQueryParams(queryParams, config);
		},
	},
	initialOptions: {
		fetcher: async (config) => {
			return getAllStageOptions(config);
		},
	},
});
